// This is a service that uses the Angular Hono client to make requests to the Dealcore API.
// And this is the only exception for eslint-disable-next-line @nx/enforce-module-boundaries!!!
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { DealcoreType } from '@hyundai/dealcore-lib/router';
// ********************************************************************************************
// ********************************************************************************************
import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject, type Provider } from '@angular/core';
import { Store } from '@ngrx/store';
import { injectAngularHonoClient } from '@pixelgenau/ngx-hono';
import { type Observable } from 'rxjs';
import { state } from '../+state/login/login.reducer';
import {
    ContactFormPayload,
    GeneralRequestFormPayload,
    ReservationFormPayload,
    ServiceFormPayload,
    TestDriveFormPayload,
} from './api-types';
export interface DealcoreApiConfig {
    baseUrl: string;
}

export const DEALCORE_API_CONFIG = new InjectionToken<DealcoreApiConfig>('DEALCORE_API_CONFIG');

export function provideDealcoreApiConfig(config: DealcoreApiConfig): Provider {
    return { provide: DEALCORE_API_CONFIG, useValue: config };
}

@Injectable({ providedIn: 'root' })
export class DealerApiService {
    protected readonly store = inject(Store);
    protected readonly authToken = this.store.selectSignal(state.selectAuthToken);

    protected readonly http = inject(HttpClient);
    protected readonly apiConfig = inject(DEALCORE_API_CONFIG);
    protected readonly api = injectAngularHonoClient<DealcoreType>({
        url: this.apiConfig.baseUrl,
        authToken: this.authToken,
        withCredentials: true,
    });

    protected postAuth<T>(url: string, body: FormData | Record<string, unknown>): Observable<T> {
        return this.http.post<T>(`${this.apiConfig.baseUrl}/${url}`, body, {
            // return this.http.post<T>(url, body, {
            headers: this.authToken()
                ? {
                      Authorization: `Bearer ${this.authToken()}`,
                  }
                : undefined,
        });
    }

    async getDealerBySlug(slug: string) {
        const request = await this.api[':slug'].dealer.$get({ param: { slug } });
        return request.json();
    }

    async getVehicleMarketDealerBySlug(slug: string) {
        const request = await this.api[':slug'].dealer['vehicle-market'].$get({ param: { slug } });
        return request.json();
    }

    async getGeneralServices(slug: string) {
        const request = await this.api[':slug'].services.generalServices.$get({
            param: { slug },
        });
        return request.json();
    }

    async getServiceForm(slug: string) {
        const request = await this.api[':slug'].services.serviceForm.$get({
            param: { slug },
        });
        return request.json();
    }

    async getServicesHero(slug: string) {
        const request = await this.api[':slug'].services.heroServices.$get({
            param: { slug },
        });
        return request.json();
    }

    async getModelList(dealerSlug: string) {
        const request = await this.api[':slug'].models.$get({
            param: { slug: dealerSlug },
        });
        return request.json();
    }

    async getFirstModelAnnouncementCampaign(dealerSlug: string) {
        const request = await this.api[':slug'].campaigns['model-anouncement'].$get({
            param: { slug: dealerSlug },
        });
        return request.json();
    }

    async getModelDetail(dealerSlug: string, modelSlug: string) {
        const request = await this.api[':slug'].models[':modelSlug'].$get({
            param: { slug: dealerSlug, modelSlug },
        });
        return request.json();
    }

    async getCampaignsOverview(slug: string) {
        const request = await this.api[':slug'].campaigns.overview.$get({
            param: {
                slug,
            },
        });
        return request.json();
    }

    async getCampaignDetail(dealerSlug: string, campaignSlug: string) {
        const request = await this.api[':slug'].campaigns.detail[':campaignSlug'].$get({
            param: { slug: dealerSlug, campaignSlug },
        });
        return request.json();
    }

    async getPageWarranty(dealerSlug: string) {
        const request = await this.api[':slug'].pages.warranty.$get({
            param: { slug: dealerSlug },
        });
        return request.json();
    }

    async getPagePromise(dealerSlug: string) {
        const request = await this.api[':slug'].pages['hyundai-promise'].$get({
            param: { slug: dealerSlug },
        });
        return request.json();
    }

    async getPageBusinessCustomer(dealerSlug: string) {
        const request = await this.api[':slug'].pages['business-customer'].$get({
            param: { slug: dealerSlug },
        });
        return request.json();
    }

    async getHomePage(dealerSlug: string) {
        const request = await this.api[':slug'].pages.home.$get({
            param: { slug: dealerSlug },
        });
        return request.json();
    }

    async getAboutUsContent(slug: string) {
        const request = await this.api[':slug']['about-us'].content.$get({
            param: { slug },
        });
        return request.json();
    }

    async getShopContent(slug: string) {
        const request = await this.api[':slug'].pages.shop.$get({
            param: { slug },
        });
        return request.json();
    }

    async getImprintContent(slug: string) {
        const request = await this.api[':slug'].pages.imprint.$get({
            param: { slug },
        });
        return request.json();
    }

    async getPrivacyContent(slug: string) {
        const request = await this.api[':slug'].pages.privacy.$get({
            param: {
                slug,
            },
        });
        return request.json();
    }

    async getAboutUsTeasers(slug: string) {
        const request = await this.api[':slug']['about-us'].teaser.$get({
            param: { slug },
        });
        return request.json();
    }

    async getJobOfferContent(slug: string) {
        const request = await this.api[':slug']['about-us']['job-offer'].$get({
            param: { slug },
        });
        return request.json();
    }

    async getAboutUsEmployees(slug: string) {
        const request = await this.api[':slug']['about-us']['employees'].$get({
            param: { slug },
        });
        return request.json();
    }

    async getDealerList() {
        const request = await this.api.sitemap.list.$get();
        return request.json();
    }

    async postServiceForm(data: ServiceFormPayload) {
        const request = await this.api[':slug']['form']['send-service-form'].$post({
            param: { slug: data.dealer },
            json: {
                ...data,
            },
        });
        return request.json();
    }

    async getDealerCampaignDetail(dealerSlug: string, campaignSlug: string) {
        const request = await this.api[':slug']['dealer-campaigns']['detail'][':campaignSlug'].$get({
            param: { slug: dealerSlug, campaignSlug },
        });
        return request.json();
    }

    async postContactForm(slug: string, data: ContactFormPayload) {
        const request = await this.api[':slug']['form']['send-contact-form'].$post({
            param: { slug },
            json: {
                ...data,
            },
        });
        return request.json();
    }

    async postTestDriveForm(data: TestDriveFormPayload) {
        const request = await this.api['checkout']['send-testdrive-form']['$post']({
            json: {
                ...data,
            },
        });
        return request.json();
    }

    async postGeneralRequestForm(data: GeneralRequestFormPayload) {
        const request = await this.api['checkout']['send-generalrequest-form']['$post']({
            json: {
                ...data,
            },
        });
        return request.json();
    }

    async postReservationForm(data: ReservationFormPayload) {
        const request = await this.api['checkout']['send-reservation-form']['$post']({
            json: {
                ...data,
            },
        });
        return request.json();
    }

    async geoSearch(search: string) {
        const request = await this.api.geo.search[':search'].$get({ param: { search } });
        return request.json();
    }

    async byCordSearch(lat: number, lng: number) {
        const request = await this.api.geo.cord.$get({ query: { lat: lat.toString(), lng: lng.toString() } });
        return request.json();
    }
}
